// @givmo/passkeys — what the signed-in account holds, read from the server's own records
// (AS-WEB10, AS-WEB11).
//
//   - its passkeys: `GET /users/me/passkeys` (404 while the backend's passkey surface is dark);
//   - whether it holds an authenticator app: `GET /users/me/security`'s `mfa.totp_enrolled` — the
//     only honest "is this account two-factor" signal, because it is what the SERVER saw. Served
//     while either the two-factor or the passkey flag is on (AS-BE47); 404 otherwise.
//
// ⚠ KEY ON A 200 ARRIVING. Until a read has answered 200, a dark 404, a 403 and a transport failure
// all read as "not served": the passkey arm and the Sign-in & security card then render nothing, and
// `holdsAuthenticator` stays `undefined` — never `false`, which a caller would read as a fact about
// the account. No retries: while dark the 404 is the expected answer, and retrying it only
// multiplies requests.
//
// ⚠ ONCE SERVED, ONLY THE DARK 404 WITHDRAWS IT (`servedData`). A failed RE-read keeps the last 200's
// answer: TanStack Query keeps that `data` but reports the query as errored, and keying on
// `isSuccess` let one transient failure of the re-read that follows a rename, an add or a removal
// hide the card, its account-menu row and the sheet's passkey arm, so a passkey-only prompt met a
// dead end (cut 1003a's review, F2).
//
// ⚠ A SURFACE RE-READS ON EVERY RETURN TO THE WINDOW, stale or not (`refetchOnWindowFocus:
// 'always'`). The account changes elsewhere: a passkey added in the Givmo app or another tab must show
// on the Sign-in & security card the person comes back to. The surfaces also re-read after any failed
// add or removal (`invalidateAccountFactors`), so a refusal resting on a stale answer heals on the
// next attempt.
//
// ⚠ A "CONFIRM IT'S YOU" HOST READS ONLY WHEN A PROMPT OPENS (AS-WEB15). A console may mount its
// step-up host once at the shell (Compliance's signing cut does), so a host that read on render would
// put both reads on every page and every window focus — and on staging and prod, while the flags are
// dark, a 404 each time. So a host OBSERVES the cache (`useStepUpHostFactors`: no request of its own)
// and reads when asked (`prepare`): the gate asks as a refusal opens a prompt, and decides on that
// read, so a passkey added elsewhere is seen before the act, and the gate's at-once failure of a
// passkey-only prompt for an account with none (Board's Sign then offers setup) rests on the
// account's answer, never on a stale or missing one. The Sign-in & security card keeps its own reads,
// and the host sees them through the cache. ⚠ Invalidating does NOT refetch an entry only a host
// observes (TanStack refetches active queries only): after a change a host must see before any
// prompt — a passkey just added — call its `prepare`.
//
// ⚠ THE CONSOLES' ACCOUNT MENU READS ONLY WHEN SOMEONE REACHES FOR IT (AS-WEB16). Both shells
// draw a "Sign-in & security" row while the passkey surface is served, and both used to read the
// list on every page and every window focus to know it — a 404 per page on staging and prod while
// passkeys are dark. The menu now observes the cache (`useMyPasskeysOnDemand`: no request of its own
// on render or window focus) and reads as the person reaches for it: the pointer entering its
// trigger, the trigger taking keyboard focus, the menu opening. A read younger than a minute is
// reused, the dark 404 too (by hand: `useMyPasskeysOnDemand`). Nothing else depends on the shell's
// read: a host reads through `prepare`, and the card and its page read for themselves.
//
// ⚠ ONE ACCOUNT'S ANSWER NEVER SERVES ANOTHER. The signed-in uid is the last part of each key, so a
// sign-in on a shared device can never paint the previous account's passkeys, even before the host
// clears its cache on sign-out.
//
// The reads call the generated operations directly (`sdk.*`) rather than through the generated
// query options, so a host whose test harness drives only some of `sdk` meets an ordinary failed
// read here — "not served" — rather than a missing module export.

import { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';
import { useQuery, useQueryClient, type QueryClient, type QueryState } from '@tanstack/react-query';
import { isGivmoError, sdk, type PasskeyOut, type UserSecurityOut } from '@givmo/api-client';

/** How long a read may be trusted before a background refetch: a minute, like the shell's reads. */
const STALE_MS = 60_000;

/** The passkey list's key family (every account's entry), for invalidating after a change. */
export const MY_PASSKEYS_QUERY_KEY = ['passkeys', 'my-passkeys'] as const;

/** The security read's key family (every account's entry). */
export const MY_SECURITY_QUERY_KEY = ['passkeys', 'my-security'] as const;

/** The rows of the list body, bare or under `data`; `[]` for any other shape. Never throws. */
export function passkeyRows(body: unknown): PasskeyOut[] {
  if (Array.isArray(body)) return body as PasskeyOut[];
  const data = (body as { data?: unknown } | null | undefined)?.data;
  return Array.isArray(data) ? (data as PasskeyOut[]) : [];
}

/** Whether a hook reads for itself (the card, the menu rows) or only observes the cache (a host). */
export interface AccountReadOptions {
  /** Default `true`. `false`: no request of its own, ever; it shows what the cache holds. */
  read?: boolean;
}

async function fetchMyPasskeys({ signal }: { signal?: AbortSignal }): Promise<PasskeyOut[]> {
  const { data } = await sdk.passkeysListMyPasskeys({ signal, throwOnError: true });
  return passkeyRows(data);
}

async function fetchMySecurity({ signal }: { signal?: AbortSignal }): Promise<UserSecurityOut> {
  const { data } = await sdk.userSecurityGetMySecurity({ signal, throwOnError: true });
  return data;
}

/** The account's passkeys: the list once served, `undefined` while unknown or not served here. */
export function useMyPasskeys(uid: string | null, { read = true }: AccountReadOptions = {}) {
  return useQuery({
    queryKey: [...MY_PASSKEYS_QUERY_KEY, uid],
    queryFn: fetchMyPasskeys,
    enabled: uid !== null && read,
    retry: false,
    staleTime: STALE_MS,
    refetchOnWindowFocus: read ? 'always' : false,
  });
}

/** The account's security read: its body once served, `undefined` while unknown or not served. */
export function useMySecurity(uid: string | null, { read = true }: AccountReadOptions = {}) {
  return useQuery({
    queryKey: [...MY_SECURITY_QUERY_KEY, uid],
    queryFn: fetchMySecurity,
    enabled: uid !== null && read,
    retry: false,
    staleTime: STALE_MS,
    refetchOnWindowFocus: read ? 'always' : false,
  });
}

/**
 * Read what the account holds NOW (both reads, fresh whatever the cache holds) — the read a host
 * makes when a "Confirm it's you" sheet opens. Never rejects: a 404 leaves the dark answer, and a
 * failed read keeps the last 200's (`servedData`), exactly as the reads themselves do.
 */
export async function fetchAccountFactors(queryClient: QueryClient, uid: string): Promise<void> {
  await Promise.allSettled([
    queryClient.fetchQuery({
      queryKey: [...MY_PASSKEYS_QUERY_KEY, uid],
      queryFn: fetchMyPasskeys,
      retry: false,
      staleTime: 0,
    }),
    queryClient.fetchQuery({
      queryKey: [...MY_SECURITY_QUERY_KEY, uid],
      queryFn: fetchMySecurity,
      retry: false,
      staleTime: 0,
    }),
  ]);
}

/**
 * What the security read answered NOW: its body (`served`), the dark 404 (`dark`), or no answer at all
 * (`failed`: a transport error, a timeout, any other refusal).
 */
export type SecurityReadNow =
  | { readonly answer: 'served'; readonly security: UserSecurityOut }
  | { readonly answer: 'dark' }
  | { readonly answer: 'failed' };

/**
 * Read the account's security read NOW, fresh whatever the cache holds. Never rejects. The answer
 * lands in the cache, so every surface on the key redraws from it. ⚠ A failure is `failed`, never
 * the last 200's body: a caller deciding on this read (the stale tab's re-read, AS-WEB23) must not
 * decide on the very answer it re-read to replace (cut 1009b's review, F2).
 */
export async function fetchMySecurityNow(
  queryClient: QueryClient,
  uid: string,
): Promise<SecurityReadNow> {
  try {
    const security = await queryClient.fetchQuery({
      queryKey: [...MY_SECURITY_QUERY_KEY, uid],
      queryFn: fetchMySecurity,
      retry: false,
      staleTime: 0,
    });
    return { answer: 'served', security };
  } catch (thrown) {
    return isGivmoError(thrown) && thrown.status === 404
      ? { answer: 'dark' }
      : { answer: 'failed' };
  }
}

/** The passkey list as a surface that reads on demand has it, and the read it makes when asked. */
export interface OnDemandPasskeys {
  /** The served list (`servedData`); `undefined` while unknown, or once the read answers the 404. */
  passkeys: readonly PasskeyOut[] | undefined;
  /**
   * Read the list unless the cache holds an answer younger than a minute (the card's own, or the
   * last ask's) — the dark 404 included, unless something has marked it out of date since. Never
   * rejects: a 404 leaves the dark answer, a failed read keeps the last 200's. Calls made while a
   * read is on the wire share it.
   */
  read: () => Promise<void>;
}

/**
 * The account's passkeys for a surface that reads only when asked — the consoles' account menu
 * (AS-WEB16): no request on render or window focus; it observes the cache, and `read` reads.
 *
 * ⚠ THE DARK 404 IS REUSED BY HAND (the cut-1003k review's F3). TanStack never counts an answer with
 * no data as fresh (`Query.isStaleByTime` is true whenever `data` is undefined), so `staleTime` alone
 * re-read the 404 on every reach while passkeys are dark — a request per pointer crossing and per
 * focus on staging and prod. And an error marks its query invalidated on arrival, which makes a
 * later invalidation a no-op that leaves no trace. So once its own read lands the 404, `read` clears
 * that mark: an invalidation (`invalidateAccountFactors`, or any on the key) then shows, and the next
 * reach reads again; so does the minute passing.
 */
export function useMyPasskeysOnDemand(uid: string | null): OnDemandPasskeys {
  const queryClient = useQueryClient();
  const passkeys = servedData(useMyPasskeys(uid, { read: false }));
  const read = useCallback(async () => {
    if (uid === null) return;
    const queryKey = [...MY_PASSKEYS_QUERY_KEY, uid];
    if (darkAnswerHolds(queryClient.getQueryState(queryKey))) return;
    await queryClient
      .fetchQuery({ queryKey, queryFn: fetchMyPasskeys, retry: false, staleTime: STALE_MS })
      .catch(() => {
        const query = queryClient.getQueryCache().find({ queryKey, exact: true });
        if (query !== undefined && isDarkAnswer(query.state)) {
          query.setState({ isInvalidated: false });
        }
      });
  }, [queryClient, uid]);
  return { passkeys, read };
}

/** The read's last answer is the dark 404, which `servedData` shows as nothing served. */
function isDarkAnswer(state: QueryState<unknown, unknown> | undefined): boolean {
  return (
    state !== undefined &&
    state.status === 'error' &&
    isGivmoError(state.error) &&
    state.error.status === 404
  );
}

/** The dark 404 still answers: younger than a minute, and nothing has marked it out of date since. */
function darkAnswerHolds(state: QueryState<unknown, unknown> | undefined): boolean {
  return (
    state !== undefined &&
    isDarkAnswer(state) &&
    !state.isInvalidated &&
    Date.now() - state.errorUpdatedAt < STALE_MS
  );
}

/**
 * A read's answer as every surface uses it: the last 200's body, kept through a failed RE-read, and
 * `undefined` while nothing has been served or once the read answers the dark 404 (see the header).
 */
export function servedData<T>(query: { data: T | undefined; error: unknown }): T | undefined {
  if (isGivmoError(query.error) && query.error.status === 404) return undefined;
  return query.data;
}

/**
 * Re-read what the account holds (both reads, every account's entry): after any add or removal that
 * did not complete, whose refusal may have rested on a stale answer.
 */
export async function invalidateAccountFactors(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: MY_PASSKEYS_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: MY_SECURITY_QUERY_KEY }),
  ]);
}

/**
 * AS-BE63 — a removal's own answer, written into the signed-in account's served reads.
 *
 * A spent recovery code (its 204) removed the authenticator app; a finished reset (`completed`)
 * removed it and every passkey, and its pending reset with them. The backend then ended every
 * session of the account, so neither read can be made again — each would answer 401 — while the
 * surfaces behind the result (the Sign-in & security card) stay on screen until the console signs
 * out. They take the server's answer to the removal instead, which is what a re-read would have
 * said. Only `uid`'s entries, and only ones already served: nothing is written for an account
 * never read, and one account's answer never serves another.
 */
export function recordFactorsRemoved(
  queryClient: QueryClient,
  uid: string,
  removed: { readonly passkeysAndReset: boolean },
): void {
  queryClient.setQueryData<UserSecurityOut>([...MY_SECURITY_QUERY_KEY, uid], (security) =>
    security === undefined
      ? undefined
      : {
          ...security,
          mfa: {
            ...security.mfa,
            totp_enrolled: false,
            totp_enrolled_at: null,
            ...(removed.passkeysAndReset ? { pending_factor_reset: null } : {}),
          },
        },
  );
  if (removed.passkeysAndReset) {
    queryClient.setQueryData<PasskeyOut[]>([...MY_PASSKEYS_QUERY_KEY, uid], (list) =>
      list === undefined ? undefined : [],
    );
  }
}

/**
 * The blocks a NARROW route of the security read leaves null: the factor-reset cancel (the one the
 * web calls) states the account's factors, not these. Its null means "not stated", never "not
 * locked" or "no employer" (backend `employer_sso.md` §10).
 */
const NARROW_ANSWER_UNSTATED = ['sign_in_lock', 'employer_sso', 'employer_policy'] as const;

/**
 * AS-WEB13/AS-WEB23 — the factor-reset cancel's own answer, written into the account's security read.
 *
 * Everything is the cancel's, except the blocks it does not state: where its answer leaves one of
 * them null or absent, the last full read's statement stays. Written wholesale, its
 * `sign_in_lock: null` read as "not locked" and turned a lock-bound card back into today's card,
 * every button on offer, for an account still locked (cut 1009b's review, F1). Returns the object the
 * cache now holds, so the notice says what stayed only while the account still reads that way.
 */
export function recordFactorResetCancelled(
  queryClient: QueryClient,
  uid: string | null,
  answer: UserSecurityOut,
): UserSecurityOut | undefined {
  const queryKey = [...MY_SECURITY_QUERY_KEY, uid];
  queryClient.setQueryData<UserSecurityOut>(queryKey, (previous) => {
    if (previous === undefined) return answer;
    const merged: Record<string, unknown> = { ...answer };
    const stated = previous as unknown as Record<string, unknown>;
    for (const key of NARROW_ANSWER_UNSTATED) {
      if ((merged[key] === null || merged[key] === undefined) && key in stated) {
        merged[key] = stated[key];
      }
    }
    return merged as unknown as UserSecurityOut;
  });
  return queryClient.getQueryData<UserSecurityOut>(queryKey);
}

/** `mfa.totp_enrolled` from a served security read, or `undefined` when there is none to read. */
export function holdsAuthenticatorFrom(security: UserSecurityOut | undefined): boolean | undefined {
  const enrolled = (security as { mfa?: { totp_enrolled?: unknown } } | undefined)?.mfa
    ?.totp_enrolled;
  return typeof enrolled === 'boolean' ? enrolled : undefined;
}

/**
 * Whether the backend serves authenticator apps here (`capabilities.mfa.enabled`, the two-factor
 * flag alone since AS-BE47): `false` while the read is not served or says off.
 */
export function authenticatorAppsServedFrom(security: UserSecurityOut | undefined): boolean {
  const enabled = (security as { capabilities?: { mfa?: { enabled?: unknown } } } | undefined)
    ?.capabilities?.mfa?.enabled;
  return enabled === true;
}

/** What the account holds, for a host adapter and the Sign-in & security card. */
export interface AccountFactors {
  /** The passkeys, once the list is served; `undefined` while unknown or dark. */
  passkeys: readonly PasskeyOut[] | undefined;
  /** Whether it holds at least one passkey; `undefined` while the list is unknown or dark. */
  holdsPasskey: boolean | undefined;
  /** Whether it holds an authenticator app; `undefined` while the security read is unknown or dark. */
  holdsAuthenticator: boolean | undefined;
  /**
   * The security read's body once served (`undefined` while unknown or dark): its pending reset,
   * recovery codes and capabilities. Served under either flag (AS-BE47), so the account reset is
   * available exactly where this is defined.
   */
  security: UserSecurityOut | undefined;
}

/** The signed-in account's factors (both reads, each keyed by `uid`). */
export function useAccountFactors(
  uid: string | null,
  options: AccountReadOptions = {},
): AccountFactors {
  const passkeys = useMyPasskeys(uid, options);
  const security = servedData(useMySecurity(uid, options));
  const list = servedData(passkeys);
  return {
    passkeys: list,
    holdsPasskey: list === undefined ? undefined : list.length > 0,
    holdsAuthenticator: holdsAuthenticatorFrom(security),
    security,
  };
}

/** What a step-up host holds: the factors as the cache has them, and the read it makes on demand. */
export interface StepUpHostFactors extends AccountFactors {
  /** `StepUpHostAdapter.prepare`: read both now; resolves once what was read has rendered. */
  prepare: () => Promise<void>;
  /** `StepUpHostAdapter.preparing`: a `prepare` read is on the wire. */
  preparing: boolean;
}

/**
 * A "Confirm it's you" host's factors (AS-WEB15): no request of its own on render or focus — it
 * observes the cache (where the card's own reads land too) and reads when asked (`prepare`: the gate,
 * as a prompt opens; a module deciding before one).
 */
export function useStepUpHostFactors(uid: string | null): StepUpHostFactors {
  const queryClient = useQueryClient();
  const factors = useAccountFactors(uid, { read: false });
  const [inFlight, setInFlight] = useState(0);
  const prepare = useCallback(async () => {
    if (uid === null) return;
    setInFlight((count) => count + 1);
    try {
      await fetchAccountFactors(queryClient, uid);
    } finally {
      // Render what was read BEFORE resolving: the gate opens its sheet on the next line, from the
      // adapter this host hands it, and must find the account's answer there — not the facts from
      // before the read, which the cache's own notice to this host has not delivered yet.
      flushSync(() => setInFlight((count) => count - 1));
    }
  }, [queryClient, uid]);
  return { ...factors, prepare, preparing: inFlight > 0 };
}
